import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Container, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  Select, Input, Button, Flex, Text, Spinner, Card, CardBody, useToast,
  Stat, StatLabel, StatNumber, StatGroup, InputGroup, InputLeftElement,
  IconButton, VStack, Divider,
} from '@chakra-ui/react';
import { FiRefreshCw, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const ContasReceber = () => {
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.status) params.set('status', filters.status);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);

      const res = await fetch(`${API_BASE_URL}api/payment/admin/history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Falha ao buscar dados');
      const data = await res.json();
      setPayments(data.payments || []);
      setPagination(data.pagination || { page: 1, total: 0, totalPages: 1 });
      setStats(data.stats || null);
    } catch (err) {
      toast({ title: 'Erro', description: err.message, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    } finally {
      setLoading(false);
    }
  }, [page, filters, toast]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Compute accounting summaries from loaded payments
  const accounting = React.useMemo(() => {
    let received = 0, pending = 0, overdue = 0, refunded = 0;
    const now = new Date();

    payments.forEach(p => {
      const amount = p.amount || 0;
      if (p.status === 'paid') received += amount;
      else if (['pending', 'waiting_payment', 'processing'].includes(p.status)) {
        // Check if overdue (boleto with due date passed)
        if (p.boleto?.dueDate && new Date(p.boleto.dueDate) < now) {
          overdue += amount;
        } else {
          pending += amount;
        }
      } else if (p.status === 'refunded' || p.status === 'chargedback') {
        refunded += amount;
      }
    });

    return { received, pending, overdue, refunded };
  }, [payments]);

  // Monthly chart from loaded payments
  const monthlyData = React.useMemo(() => {
    const byMonth = {};
    payments.forEach(p => {
      const d = new Date(p.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) byMonth[key] = { month: key, recebido: 0, pendente: 0, estornado: 0 };
      const amount = p.amount || 0;
      if (p.status === 'paid') byMonth[key].recebido += amount;
      else if (['pending', 'waiting_payment', 'processing'].includes(p.status)) byMonth[key].pendente += amount;
      else if (p.status === 'refunded' || p.status === 'chargedback') byMonth[key].estornado += amount;
    });
    return Object.values(byMonth)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({ ...m, recebido: +m.recebido.toFixed(2), pendente: +m.pendente.toFixed(2), estornado: +m.estornado.toFixed(2) }));
  }, [payments]);

  const getAccountingStatus = (p) => {
    if (p.status === 'paid') return { label: 'Recebido', color: 'green' };
    if (p.status === 'refunded') return { label: 'Estornado', color: 'purple' };
    if (p.status === 'chargedback') return { label: 'Contestado', color: 'orange' };
    if (['pending', 'waiting_payment', 'processing'].includes(p.status)) {
      if (p.boleto?.dueDate && new Date(p.boleto.dueDate) < new Date()) {
        return { label: 'Vencido', color: 'red' };
      }
      return { label: 'A Receber', color: 'yellow' };
    }
    if (p.status === 'failed' || p.status === 'declined') return { label: 'Falhou', color: 'red' };
    return { label: p.status, color: 'gray' };
  };

  return (
    <Container maxW="container.xl" py={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Contas a Receber</Heading>
          <Text color="gray.500" fontSize="sm">Receitas de assinaturas de profissionais</Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={fetchPayments} isLoading={loading}>
          Atualizar
        </Button>
      </Flex>

      {/* Summary Cards */}
      <StatGroup mb={6}>
        <Card flex={1} mr={3}><CardBody py={3}>
          <Stat><StatLabel>Recebido</StatLabel><StatNumber fontSize="lg" color="green.500">{formatCurrency(accounting.received)}</StatNumber></Stat>
        </CardBody></Card>
        <Card flex={1} mr={3}><CardBody py={3}>
          <Stat><StatLabel>A Receber</StatLabel><StatNumber fontSize="lg" color="yellow.600">{formatCurrency(accounting.pending)}</StatNumber></Stat>
        </CardBody></Card>
        <Card flex={1} mr={3}><CardBody py={3}>
          <Stat><StatLabel>Vencido</StatLabel><StatNumber fontSize="lg" color="red.500">{formatCurrency(accounting.overdue)}</StatNumber></Stat>
        </CardBody></Card>
        <Card flex={1}><CardBody py={3}>
          <Stat><StatLabel>Estornado</StatLabel><StatNumber fontSize="lg" color="purple.500">{formatCurrency(accounting.refunded)}</StatNumber></Stat>
        </CardBody></Card>
      </StatGroup>

      {/* Monthly Chart */}
      {monthlyData.length > 0 && (
        <Card mb={6}>
          <CardBody>
            <Heading size="sm" mb={3}>Contas a Receber por Mês</Heading>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$${v}`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="recebido" name="Recebido" fill="#38A169" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pendente" name="Pendente" fill="#ECC94B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="estornado" name="Estornado" fill="#9F7AEA" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card mb={4}>
        <CardBody py={3}>
          <HStack spacing={3} wrap="wrap">
            <Select size="sm" maxW="180px" placeholder="Todos os status" value={filters.status}
              onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
              <option value="paid">Recebido</option>
              <option value="pending">A Receber</option>
              <option value="refunded">Estornado</option>
              <option value="chargedback">Contestado</option>
            </Select>
            <InputGroup size="sm" maxW="170px">
              <InputLeftElement pointerEvents="none" fontSize="xs" color="gray.400">De</InputLeftElement>
              <Input type="date" pl="8" value={filters.startDate} onChange={(e) => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(1); }} />
            </InputGroup>
            <InputGroup size="sm" maxW="170px">
              <InputLeftElement pointerEvents="none" fontSize="xs" color="gray.400">Até</InputLeftElement>
              <Input type="date" pl="8" value={filters.endDate} onChange={(e) => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(1); }} />
            </InputGroup>
            {(filters.status || filters.startDate || filters.endDate) && (
              <Button size="sm" variant="ghost" onClick={() => { setFilters({ status: '', startDate: '', endDate: '' }); setPage(1); }}>Limpar</Button>
            )}
          </HStack>
        </CardBody>
      </Card>

      {/* Table */}
      <Card>
        <CardBody p={0}>
          {loading ? (
            <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
          ) : payments.length === 0 ? (
            <Flex justify="center" py={10}><Text color="gray.500">Nenhum registro encontrado</Text></Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Data</Th>
                    <Th>Descrição</Th>
                    <Th>Plano</Th>
                    <Th>Vencimento</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Situação</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.map((p) => {
                    const acctStatus = getAccountingStatus(p);
                    return (
                      <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                        <Td whiteSpace="nowrap" fontSize="sm">{formatDate(p.createdAt)}</Td>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">{p.description}</Text>
                        </Td>
                        <Td>
                          {p.plan ? (
                            <Badge variant="subtle" colorScheme="teal" fontSize="xs">{p.plan.name}</Badge>
                          ) : '-'}
                        </Td>
                        <Td fontSize="sm">
                          {p.periodEnd ? formatDate(p.periodEnd) : p.boleto?.dueDate ? formatDate(p.boleto.dueDate) : '-'}
                        </Td>
                        <Td isNumeric fontWeight="medium" fontSize="sm">{formatCurrency(p.amount)}</Td>
                        <Td>
                          <Badge colorScheme={acctStatus.color} fontSize="xs">{acctStatus.label}</Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}

          {pagination.totalPages > 1 && (
            <Flex justify="space-between" align="center" p={3} borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="sm" color="gray.500">
                Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
              </Text>
              <HStack>
                <IconButton size="sm" icon={<FiChevronLeft />} isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} />
                <IconButton size="sm" icon={<FiChevronRight />} isDisabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)} />
              </HStack>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default ContasReceber;
